import type { ReactNode } from "react";
import { Popover } from "@base-ui/react/popover";
import { IconFilter, IconFilterFilled } from "@tabler/icons-react";
import Card from "../UI/Card";
import Button from "../UI/Button";
import CheckboxStandalone from "../UI/CheckboxStandalone";
import CountBadge from "../UI/CountBadge";
import Text from "../UI/Text";
import useDialogPortalContainer from "../../hooks/use-dialog-portal-container";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import styles from "./FilterableColumnHeader.module.css";

export interface ColumnFilterOption {
  value: string;
  label: ReactNode;
}

interface FilterableColumnHeaderProps {
  label: string;
  options: ColumnFilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
}

/**
 * En-tête de colonne des tableaux de pilotage : le titre et une icône qui
 * ouvre le choix des statuts à afficher pour cette seule colonne.
 */
export default function FilterableColumnHeader({
  label,
  options,
  values,
  onChange,
}: FilterableColumnHeaderProps) {
  const { container, triggerRef } = useDialogPortalContainer();
  const active = values.length > 0;

  const toggle = (value: string) =>
    onChange(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value],
    );

  return (
    <div className={styles.header}>
      <Text as="span" weight="medium">
        {label}
      </Text>
      <Popover.Root>
        <Popover.Trigger
          ref={triggerRef}
          className={classnames(styles.trigger, active && styles.active)}
          aria-label={`Filtrer la colonne ${label}`}
        >
          {active ? (
            <IconFilterFilled size={16} />
          ) : (
            <IconFilter size={16} stroke={1.5} />
          )}
          {active && (
            <CountBadge count={values.length} className={styles.badge} />
          )}
        </Popover.Trigger>
        <Popover.Portal container={container}>
          <Popover.Positioner
            sideOffset={6}
            align="start"
            className={styles.positioner}
          >
            <Popover.Popup
              className={styles.popup}
              render={<Card padding="xs" />}
            >
              <Text as="span" color="muted">
                {label}
              </Text>
              <div className={styles.list}>
                {options.map((option) => (
                  <CheckboxStandalone
                    key={option.value}
                    className={styles.option}
                    checked={values.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  >
                    {option.label}
                  </CheckboxStandalone>
                ))}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                disabled={!active}
                onClick={() => onChange([])}
              >
                Effacer
              </Button>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
